import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Collapse,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckIcon from "@mui/icons-material/Check";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FiSend } from "react-icons/fi";
import { theme } from "../../theme/colors";
import { buildQrDataUrl } from "../../utils/qrCode";

/**
 * Share sheet for a CivixThread profile: copy link / native share first,
 * with an optional QR code for print or in-person sharing.
 */
export default function ShareProfileModal({
  open,
  onClose,
  profileUrl,
  displayName = "",
  title = "Share profile",
  footerNote = null,
  onCopied,
  onCopyFailed,
}) {
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [qrError, setQrError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  useEffect(() => {
    if (!open || !profileUrl || !showQr) return undefined;

    let cancelled = false;
    setQrError(false);

    buildQrDataUrl(profileUrl, 360)
      .then((dataUrl) => {
        if (!cancelled) setQrDataUrl(dataUrl);
      })
      .catch(() => {
        if (!cancelled) setQrError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [open, profileUrl, showQr]);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      setShowQr(false);
      setQrDataUrl("");
      setQrError(false);
    }
  }, [open]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      onCopied?.();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onCopyFailed?.();
    }
  };

  const handleNativeShare = async () => {
    try {
      await navigator.share({ title: displayName || "CivixThread profile", url: profileUrl });
    } catch (err) {
      if (err?.name !== "AbortError") onCopyFailed?.();
    }
  };

  const canNativeShare = typeof navigator !== "undefined" && Boolean(navigator.share);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      aria-labelledby="share-profile-title"
      PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close"
        size="small"
        sx={{ position: "absolute", top: 8, right: 8, color: "#0f172a" }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <DialogTitle id="share-profile-title" sx={{ textAlign: "center", pt: 2, pb: 0, px: 4 }}>
        <Typography
          component="h2"
          sx={{ fontWeight: 800, fontSize: 18, color: "#0f172a", lineHeight: 1.3 }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2} alignItems="stretch">
          <Typography sx={{ fontSize: 14, color: "#334155", textAlign: "center" }}>
            Copy your profile link to share it anywhere.
          </Typography>

          <Box
            sx={{
              width: "100%",
              px: 1.5,
              py: 1.25,
              borderRadius: 2,
              border: "1px solid #e2e8f0",
              bgcolor: "#f8fafc",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                color: "#0f172a",
                wordBreak: "break-all",
              }}
            >
              {profileUrl}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
            <Button
              onClick={handleCopy}
              variant="contained"
              fullWidth
              startIcon={copied ? <CheckIcon /> : <ContentCopyOutlinedIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                bgcolor: theme.primary,
                borderRadius: 2,
                "&:hover": { bgcolor: theme.primary },
              }}
            >
              {copied ? "Copied" : "Copy link"}
            </Button>

            {canNativeShare && (
              <Button
                onClick={handleNativeShare}
                variant="outlined"
                fullWidth
                startIcon={<FiSend size={16} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderColor: "#cbd5e1",
                  color: "#0f172a",
                  borderRadius: 2,
                  "&:hover": { borderColor: "#94a3b8", bgcolor: "#f8fafc" },
                }}
              >
                Share
              </Button>
            )}
          </Stack>

          {footerNote && (
            <Typography sx={{ fontSize: 12.5, color: "#334155", textAlign: "center" }}>
              {footerNote}
            </Typography>
          )}

          <Box sx={{ borderTop: "1px solid #e2e8f0", pt: 1.5 }}>
            <Button
              onClick={() => setShowQr((v) => !v)}
              fullWidth
              startIcon={<QrCode2OutlinedIcon sx={{ fontSize: 18 }} />}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    fontSize: 20,
                    transform: showQr ? "rotate(180deg)" : "none",
                    transition: "transform 150ms ease",
                  }}
                />
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                color: "#0f172a",
                justifyContent: "space-between",
                px: 1,
                "&:hover": { bgcolor: "#f8fafc" },
              }}
            >
              {showQr ? "Hide QR code" : "Show QR code"}
            </Button>

            <Collapse in={showQr}>
              <Stack spacing={1.5} alignItems="center" sx={{ pt: 1.5, pb: 0.5 }}>
                <Box
                  sx={{
                    width: 180,
                    height: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    bgcolor: "#fff",
                  }}
                >
                  {qrDataUrl ? (
                    <Box
                      component="img"
                      src={qrDataUrl}
                      alt={`QR code linking to ${displayName || "this"} profile`}
                      sx={{ width: "100%", height: "100%", borderRadius: 2 }}
                    />
                  ) : qrError ? (
                    <Typography sx={{ fontSize: 13, color: "#334155", px: 2, textAlign: "center" }}>
                      Couldn&apos;t generate a QR code. You can still copy the link above.
                    </Typography>
                  ) : (
                    <CircularProgress size={28} sx={{ color: theme.primary }} />
                  )}
                </Box>
                <Typography sx={{ fontSize: 12.5, color: "#334155", textAlign: "center" }}>
                  Scan to open this profile
                </Typography>
              </Stack>
            </Collapse>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
